import { motion } from "framer-motion";
import { Brain, MessageSquareHeart, Compass, Siren, CalendarClock, ArrowRight, Sparkles } from "lucide-react";
import { useNavigate } from "react-router-dom";

type Widget = {
  icon: typeof Brain;
  title: string;
  desc: string;
  cta: string;
  gradient: string;
  path?: string;
  chatPrefill?: string;
};

const widgets: Widget[] = [
  {
    icon: Brain,
    title: "AI Symptom Checker",
    desc: "Describe your symptoms — get a clinical-grade preliminary analysis in seconds.",
    cta: "Start checkup",
    gradient: "from-primary to-medical-blue-light",
    chatPrefill: "I want to check my symptoms",
  },
  {
    icon: MessageSquareHeart,
    title: "Health Assistant",
    desc: "Chat 24/7 for medication info, lab guidance and specialty referrals.",
    cta: "Open assistant",
    gradient: "from-healing-green to-healing-green-light",
    chatPrefill: "",
  },
  {
    icon: Compass,
    title: "Smart Doctor Match",
    desc: "AI matches the right specialist for your condition and location.",
    cta: "Find doctor",
    gradient: "from-primary to-healing-green",
    path: "/listings?category=2",
  },
  {
    icon: Siren,
    title: "Emergency Finder",
    desc: "Instantly locate the nearest ambulance, ER and blood bank.",
    cta: "Find emergency",
    gradient: "from-cta-orange to-destructive",
    path: "/ambulance",
  },
  {
    icon: CalendarClock,
    title: "Consultation Inquiry",
    desc: "Send inquiries to verified doctors and specialists in your city.",
    cta: "Send Inquiry",
    gradient: "from-medical-blue-light to-healing-green",
    path: "/listings?category=2",
  },
];

const openChat = (prefill?: string) => {
  window.dispatchEvent(new CustomEvent('patakare:open-chat', { detail: { prefill: prefill ?? '' } }));
};

const AIHealthcareSection = () => {
  const navigate = useNavigate();
  return (
    <section className="py-16 sm:py-20 bg-gradient-to-b from-background via-primary/5 to-background relative overflow-hidden">
      <div className="absolute top-1/2 left-0 w-[400px] h-[400px] bg-primary/10 rounded-full blur-3xl -translate-y-1/2 pointer-events-none" />
      <div className="absolute top-1/2 right-0 w-[400px] h-[400px] bg-healing-green/10 rounded-full blur-3xl -translate-y-1/2 pointer-events-none" />

      <div className="container mx-auto px-4 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <span
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-pill text-xs font-semibold mb-4"
            style={{ background: "linear-gradient(90deg, hsl(213 69% 39% / 0.1), hsl(152 62% 44% / 0.1))" }}
          >
            <Sparkles className="w-3.5 h-3.5 text-cta-orange" />
            <span className="text-gradient">AI-Powered Healthcare</span>
          </span>
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-3">
            Your <span className="text-gradient">Intelligent Health</span> Companion
          </h2>
          <p className="text-muted-foreground text-base sm:text-lg max-w-2xl mx-auto">
            Five futuristic AI tools that make finding, understanding and accessing care effortless.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {widgets.map((w, i) => {
            const Icon = w.icon;
            const featured = i === 0;
            return (
              <motion.button
                key={w.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                whileHover={{ y: -6 }}
                onClick={() => w.chatPrefill !== undefined ? openChat(w.chatPrefill) : navigate(w.path!)}
                className={`group text-left relative overflow-hidden rounded-3xl p-6 sm:p-7 bg-card/90 backdrop-blur-xl border border-border/60 shadow-card hover:shadow-card-hover transition-all ${
                  featured ? "lg:col-span-2" : ""
                }`}
              >
                <div className={`absolute -top-10 -right-10 w-40 h-40 rounded-full bg-gradient-to-br ${w.gradient} opacity-10 blur-2xl group-hover:opacity-20 transition-opacity`} />
                <div className={`relative w-14 h-14 rounded-2xl bg-gradient-to-br ${w.gradient} flex items-center justify-center mb-5 shadow-lg`}>
                  <Icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-lg sm:text-xl font-bold mb-2 group-hover:text-primary transition-colors">{w.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed mb-5 max-w-md">{w.desc}</p>
                <span className="inline-flex items-center gap-1.5 text-primary text-sm font-semibold">
                  {w.cta}
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </span>
              </motion.button>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default AIHealthcareSection;
